import { useCanvasRoute } from "@/pages/canvas/context/CanvasRouteContext";
import { useEditThreadMetadata } from "@liveblocks/react";
import { CommentPin } from "@liveblocks/react-ui";
import { ViewportPortal, useReactFlow, useStore } from "@xyflow/react";
import { MessageCircle } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useMemo, useRef, useState } from "react";

import {
  CanvasPinAffordance,
  PIN_ICON_SIZE,
  PIN_LAYER_Z_INDEX,
} from "../components/CanvasPinAffordance";
import { usePublishCanvasCommentsMirror } from "../context/CanvasCommentsMirrorContext";
import { nodeBounds } from "../utils/nodeBounds";
import {
  CanvasCommentAvatar,
  CanvasCommentPreviewCard,
} from "./CanvasCommentPreviewCard";
import { useCanvasComments } from "./CanvasCommentsContext";
import { selectCommentPreview, type CommentPreview } from "./commentPreview";
import {
  absoluteNodePosition,
  placementForDrop,
  placementPatchForDragStop,
  resolvePinPosition,
  type NodeBounds,
} from "./threadPlacement";
import { useCanvasCommentOrphanSweep } from "./useCanvasCommentOrphanSweep";
import { useCanvasCommentThreads } from "./useCanvasCommentThreads";

/**
 * Draws one pin per unresolved thread, in flow space, and publishes the
 * unfiltered thread list to the rail's mirror.
 *
 * The board shows unresolved threads only; resolving a thread takes its pin
 * off the canvas and leaves the row in the rail. Positions come from
 * ThreadMetadata through `resolvePinPosition`, never from the server-written
 * marker node — the metadata is authoritative and the marker may lag a webhook.
 *
 * Mounted inside `<ReactFlow>` so `ViewportPortal` resolves.
 */
export function CanvasCommentPinLayer() {
  const { canvasId } = useCanvasRoute();
  const { threads, isLoading, mirrored } = useCanvasCommentThreads();
  const { clickThreadPin, openThreadId } = useCanvasComments();

  // Subscribe to the node array, not a derived Map — a selector building a new
  // object every call never compares equal and would re-render on every store
  // tick.
  const nodes = useStore((s) => s.nodes);

  usePublishCanvasCommentsMirror(canvasId, mirrored, !isLoading);
  useCanvasCommentOrphanSweep();

  const nodesById = useMemo(
    () => new Map<string, NodeBounds>(nodes.map((n) => [n.id, nodeBounds(n)])),
    [nodes],
  );

  const pins = useMemo(
    () =>
      threads
        .filter((thread) => !thread.resolved)
        .map((thread) => ({
          thread,
          preview: selectCommentPreview(thread.comments),
          ...resolvePinPosition(thread.metadata, nodesById),
        })),
    [threads, nodesById],
  );

  if (pins.length === 0) return null;

  return (
    <ViewportPortal>
      {/* One stacking rung for the whole layer — pins, their hover cards and
          the drag preview all ride it, so no pin carries a z of its own. */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          zIndex: PIN_LAYER_Z_INDEX,
        }}
      >
        {pins.map((pin) => (
          <CanvasCommentPin
            key={pin.thread.id}
            threadId={pin.thread.id}
            metadata={pin.thread.metadata}
            preview={pin.preview}
            open={pin.thread.id === openThreadId}
            x={pin.x}
            y={pin.y}
            nodesById={nodesById}
            onClick={clickThreadPin}
          />
        ))}
      </div>
    </ViewportPortal>
  );
}

type PinProps = {
  threadId: string;
  metadata: Parameters<typeof resolvePinPosition>[0];
  /** Null only while every comment on the thread is a tombstone. */
  preview: CommentPreview | null;
  /** Its thread window is up, so the pin stays collapsed under it. */
  open: boolean;
  x: number;
  y: number;
  nodesById: Map<string, NodeBounds>;
  /** A click, not a drag: opens the thread, or closes an unmoved window. */
  onClick: (threadId: string) => void;
};

/** Drag threshold in screen px below which a pointer sequence is still a click. */
const DRAG_SLOP = 3;

/**
 * The only button a pin answers to. A right-press belongs to the board: React
 * Flow pans on it, and the pin must neither capture the pointer nor stop the
 * event, or the gesture runs with the pin holding half of it.
 */
const PRIMARY_BUTTON = 0;

/** DS `dur-slow` + `ease-out` — the expand register `CanvasLanding` already uses. */
const PREVIEW_EXPAND = { duration: 0.3, ease: [0.2, 0, 0, 1] } as const;
/** Hover → rest is `dur-base` (`hover-interactive-feedback.md`): leaving is quicker. */
const PREVIEW_COLLAPSE = { duration: 0.15, ease: [0.2, 0, 0, 1] } as const;

function CanvasCommentPin({
  threadId,
  metadata,
  preview,
  open,
  x,
  y,
  nodesById,
  onClick,
}: PinProps) {
  const { screenToFlowPosition, getNodes } = useReactFlow();
  const editThreadMetadata = useEditThreadMetadata();

  // Latest pointer state lives in a ref and one scheduled frame reads it, so
  // a drag re-renders at most once per frame — the shape the node drag
  // handlers use. Nothing writes metadata until pointerup.
  const dragRef = useRef<{
    startX: number;
    startY: number;
    point: { x: number; y: number };
    moved: boolean;
  } | null>(null);
  const rafRef = useRef<number | null>(null);
  const [dragPreview, setDragPreview] = useState<{
    x: number;
    y: number;
    snapNodeId?: string;
  } | null>(null);
  const [hovered, setHovered] = useState(false);

  const flushPreview = useCallback(() => {
    rafRef.current = null;
    const drag = dragRef.current;
    if (!drag) return;
    const snapped = placementForDrop(
      drag.point,
      getNodes().map(nodeBounds),
      nodesById,
    );
    setDragPreview({
      x: drag.point.x,
      y: drag.point.y,
      snapNodeId: snapped.anchorNodeId,
    });
  }, [getNodes, nodesById]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== PRIMARY_BUTTON) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      point: { x: 0, y: 0 },
      moved: false,
    };
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      if (
        !drag.moved &&
        Math.abs(e.clientX - drag.startX) < DRAG_SLOP &&
        Math.abs(e.clientY - drag.startY) < DRAG_SLOP
      ) {
        return;
      }
      drag.moved = true;
      drag.point = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(flushPreview);
    },
    [screenToFlowPosition, flushPreview],
  );

  const endDrag = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const drag = dragRef.current;
    dragRef.current = null;
    setDragPreview(null);
    return drag;
  }, []);

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== PRIMARY_BUTTON) return;
      e.stopPropagation();
      const drag = endDrag();
      if (!drag) return;
      if (!drag.moved) {
        onClick(threadId);
        return;
      }
      // One write, at the drop. Stick and unstick are decided here and
      // nowhere else — the render path resolves an anchored pin for free.
      editThreadMetadata({
        threadId,
        metadata: placementPatchForDragStop(
          metadata,
          drag.point,
          getNodes().map(nodeBounds),
          nodesById,
        ),
      });
    },
    [
      endDrag,
      onClick,
      threadId,
      editThreadMetadata,
      metadata,
      getNodes,
      nodesById,
    ],
  );

  const snapTarget = dragPreview?.snapNodeId
    ? nodesById.get(dragPreview.snapNodeId)
    : undefined;
  const snapBounds =
    snapTarget && absoluteNodePosition(snapTarget.id, nodesById);

  // Hover expands the pin into its opening comment, and a drag carries that
  // same card — so what you are moving stays legible while you move it.
  const expanded = !!preview && !open && (hovered || dragPreview !== null);

  return (
    <>
      {snapTarget && snapBounds && (
        <div
          className="pointer-events-none absolute rounded-md ring-2 ring-canvas-selection/70"
          style={{
            left: snapBounds.x,
            top: snapBounds.y,
            width: snapTarget.width,
            height: snapTarget.height,
          }}
        />
      )}
      {/* The pin straddles its point, so the wrapper box no longer covers it:
          pointer events live on the bubble and bubble up to these handlers.
          `dragstart` is cancelled here rather than on each image — it bubbles,
          and the pointer drag below is the only drag this pin has.

          `contextmenu` is defaulted away exactly as `.react-flow__pane` does
          for its own: the native menu swallows the mouseup, and d3-zoom —
          which already took the right-button mousedown on its way to the
          renderer — would be left panning with no button down. React Flow's
          pane handler cannot do it for us; it only runs when the pane itself
          is the target. */}
      <div
        className="canvas-comments"
        data-canvas-comment-pin={threadId}
        style={{
          position: "absolute",
          left: dragPreview?.x ?? x,
          top: dragPreview?.y ?? y,
          pointerEvents: "none",
          touchAction: "none",
          // An expanded card is 256px wide and would otherwise paint under
          // any pin later in the list.
          zIndex: expanded ? 1 : undefined,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={endDrag}
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
        onDragStart={(e) => e.preventDefault()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <CanvasPinAffordance
          x={dragPreview?.x ?? x}
          y={dragPreview?.y ?? y}
          open={false}
          centered
          bubbleClassName="pointer-events-auto cursor-pointer select-none active:cursor-grabbing"
          bubble={
            // The card grows out of the collapsed pin: its bottom-left corner
            // ends on the pin's own, so the open reads the same every time.
            // The scale rides this inner element, never the counter-scaled
            // wrapper above it — CanvasPinAffordance's two-layer rule.
            <div
              className="relative"
              style={{ width: PIN_ICON_SIZE, height: PIN_ICON_SIZE }}
            >
              <CommentPin corner="bottom-left" size={PIN_ICON_SIZE}>
                {preview ? (
                  <CanvasCommentAvatar
                    userId={preview.userId}
                    className="size-8"
                  />
                ) : (
                  <MessageCircle className="size-4 text-n-50" />
                )}
              </CommentPin>
              <AnimatePresence>
                {expanded && preview && (
                  <motion.div
                    className="absolute bottom-0 left-0 origin-bottom-left"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{
                      opacity: 0,
                      scale: 0.8,
                      transition: PREVIEW_COLLAPSE,
                    }}
                    transition={PREVIEW_EXPAND}
                  >
                    <CanvasCommentPreviewCard preview={preview} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          }
        />
      </div>
    </>
  );
}
